/**
 * assetsStyled.ts — Styled-components de la tab "Assets" del Brand Page.
 *
 * Qué hace: define los styled-components de BrandTabAssets.tsx (grid de
 *   miniaturas para assets/fondos, botones de borrado/set-logo, formularios de
 *   alta por URL/upload, dividers).
 * Qué exporta: cada styled-component con `export const`.
 * Quién lo usa: components/brand/BrandTabAssets.tsx.
 */
import styled from 'styled-components';

// Grid de miniaturas cuadradas para assets y fondos.
export const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

export const AssetItem = styled.div<{ $isLogo?: boolean }>`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: ${({ theme, $isLogo }) => ($isLogo ? `2px solid ${theme.colors.primary}` : theme.borders.medium)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const DeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  ${AssetItem}:hover & {
    opacity: 1;
  }

  /* En dispositivos sin hover (touch) el botón debe ser siempre alcanzable. */
  @media (hover: none) {
    opacity: 1;
  }
`;

export const LogoBadge = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.5px;
  z-index: 5;
`;

export const SetLogoButton = styled.button`
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 5;

  &:hover {
    background-color: rgba(0, 0, 0, 0.85);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  ${AssetItem}:hover & {
    opacity: 1;
  }

  /* En dispositivos sin hover (touch) el botón debe ser siempre alcanzable. */
  @media (hover: none) {
    opacity: 1;
  }
`;

export const AddAssetForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const EmptyAssets = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

export const FileInput = styled.input`
  display: none;
`;

export const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const ClearFileButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  font-size: 16px;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const UploadErrors = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;
